import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { TabPanel, Tab } from '../../../components';
import SoapForm from './soap-form';
import Addendum from './addendum';
import Billing from './billing';
import Assessment from './assessment';
import Allergies from './allergies';
import Problems from './problems';
import Medications from './medications';
import { Encounters, Observations, Claims } from './imported-data';
import JournalHistory from './journal-history';
import Vitals from './vitals';
import Procedures from './procedures';
import Immunizations from './immunizations';
import Images from './images';
import { EVISIT_STATUS } from '../../../config/constants';
import soapIcon from '../../../../img/soap-solid.svg';

const EvisitTabs = ({
	evisit,
	user,
	onShowNotification,
	appSocket,
	isDependentEvisit,
	assessment,
	onUpdateBillingCode,
	hasBillingCodeFeature,
	onSOAPFieldChange,
	onSaveVisit,
	isUpdating,
}) => {
	const [isMaximized, setMaximized] = useState(false);
	const [hideClaims, setHideClaims] = useState(true);
	const [hideEncounters, setHideEncounters] = useState(true);
	const [hideObservations, setHideObservations] = useState(true);

	const isOpen = !evisit.end_dttm;
	const isNonDependentEvisit = !isDependentEvisit;

	const handleHideEvisit = () => {
		setMaximized(true);
	};

	const handleShowEvisit = () => {
		setMaximized(false);
	};

	const evisitId = evisit.id;
	const patientId = evisit.patient.id;
	const userId = user.id;

	return (
		<TabPanel contentClassName={cn({ fullscreen: isMaximized })} stacked>
			<Tab id="soap" title="SOAP" iconFile={soapIcon}>
				<div className="form assessment-plan">
					<SoapForm
						inProgress={evisit.evisit_status === EVISIT_STATUS.IN_PROGRESS}
						isOpen
						evisit={evisit}
						onFieldChange={onSOAPFieldChange}
						onSaveVisit={onSaveVisit}
						isUpdating={isUpdating}
					/>
				</div>
			</Tab>
			{!isOpen ? (
				<Tab id="addendum" title="Addendum" icon="fa-comment">
					<Addendum
						onExpand={handleHideEvisit}
						onContract={handleShowEvisit}
						evisitId={evisitId}
						userId={userId}
						showNotification={onShowNotification}
					/>
				</Tab>
			) : null}
			<Tab id="billing" title="Billing" icon="fa-money">
				<Billing
					evisit={evisit}
					hasBillingCodeFeature={hasBillingCodeFeature}
					userId={userId}
					onBillingCodeChosen={onUpdateBillingCode}
					isUpdating={isUpdating}
				/>
			</Tab>
			{assessment && (
				<Tab id="assessment" title="Pre-visit Assessment" icon="fa-clipboard">
					<Assessment assessment={assessment} />
				</Tab>
			)}
			<Tab id="allergies" title="Allergies" icon="fa-pagelines">
				<Allergies
					patientId={patientId}
					onExpand={handleHideEvisit}
					onContract={handleShowEvisit}
					isDependentEvisit={isDependentEvisit}
					dependentAllergies={
						isDependentEvisit
							? evisit.patient_dependent.dependent_allergies
							: null
					}
				/>
			</Tab>
			{isNonDependentEvisit && (
				<Tab id="problems" title="Problems" icon="fa-exclamation-circle">
					<Problems
						patientId={patientId}
						onExpand={handleHideEvisit}
						onContract={handleShowEvisit}
					/>
				</Tab>
			)}
			<Tab id="medications" title="Medications" icon="fa-plus-circle">
				<Medications
					patientId={patientId}
					onExpand={handleHideEvisit}
					onContract={handleShowEvisit}
					isDependentEvisit={isDependentEvisit}
					dependentMedications={
						isDependentEvisit
							? evisit.patient_dependent.dependent_medications
							: null
					}
				/>
			</Tab>
			{isNonDependentEvisit && (
				<Tab id="vitals" title="Vitals &amp; Meas." icon="fa-heartbeat">
					<Vitals
						patientId={patientId}
						onExpand={handleHideEvisit}
						onContract={handleShowEvisit}
						isDependentEvisit={isDependentEvisit}
					/>
				</Tab>
			)}
			{isNonDependentEvisit && (
				<Tab id="procedures" title="Procedures" icon="fa-scissors">
					<Procedures
						patientId={patientId}
						onExpand={handleHideEvisit}
						onContract={handleShowEvisit}
					/>
				</Tab>
			)}
			{isNonDependentEvisit && (
				<Tab id="immunizations" title="Immunizations" icon="fa-check-circle">
					<Immunizations
						patientId={patientId}
						onExpand={handleHideEvisit}
						onContract={handleShowEvisit}
					/>
				</Tab>
			)}
			{isNonDependentEvisit && (
				<Tab id="health-journal" title="Health Journal" icon="fa-history">
					<JournalHistory
						patientId={patientId}
						onExpand={handleHideEvisit}
						onContract={handleShowEvisit}
					/>
				</Tab>
			)}
			<Tab id="images" title="Visit Images" icon="fa-picture-o">
				<Images
					evisitId={evisitId}
					appSocket={appSocket}
					onExpand={handleHideEvisit}
					onContract={handleShowEvisit}
				/>
			</Tab>
			<Tab
				id="claims"
				title="Claims"
				containerClassName={cn({ hidden: hideClaims })}
				icon="fa-file-text"
			>
				<Claims
					onExpand={handleHideEvisit}
					onContract={handleShowEvisit}
					evisitId={evisitId}
					patientId={patientId}
					onDataExists={() => setHideClaims(false)}
				/>
			</Tab>
			<Tab
				id="encounters"
				title="Encounters"
				containerClassName={cn({ hidden: hideEncounters })}
				icon="fa-hospital-o"
			>
				<Encounters
					evisitId={evisitId}
					patientId={patientId}
					onExpand={handleHideEvisit}
					onContract={handleShowEvisit}
					onDataExists={() => setHideEncounters(false)}
				/>
			</Tab>
			<Tab
				id="observations"
				title="Observations"
				containerClassName={cn({ hidden: hideObservations })}
				icon="fa-stethoscope"
			>
				<Observations
					evisitId={evisitId}
					patientId={patientId}
					onExpand={handleHideEvisit}
					onContract={handleShowEvisit}
					onDataExists={() => setHideObservations(false)}
				/>
			</Tab>
		</TabPanel>
	);
};

EvisitTabs.propTypes = {
	appSocket: PropTypes.object.isRequired,
	evisit: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
	onShowNotification: PropTypes.func.isRequired,
	isDependentEvisit: PropTypes.bool,
	assessment: PropTypes.object,
	onUpdateBillingCode: PropTypes.func.isRequired,
	hasBillingCodeFeature: PropTypes.bool,
	onSOAPFieldChange: PropTypes.func,
	onSaveVisit: PropTypes.func,
	isUpdating: PropTypes.bool,
};

export default EvisitTabs;
